<template>
  <ion-grid class="form-grid">
    <ion-row v-for="(row, idx_row) in gridWithFields" :key="idx_row + stepId" :class="{ 'first-row': idx_row === 0, 'last-row': idx_row + 1 === gridWithFields.length }">
      <ion-col v-for="(col, idx_col) in row" :key="'' + idx_col + idx_row + stepId" :size-xs="maxRespGridCols.toString()" :size-sm="getFieldSize(col)" :class="{ 'first-col': idx_col === 0, 'last-col': idx_col + 1 === row.length }">
        <field v-if="col !== 'empty'" :data="col[1]" @valueChanged="updateDataForField" :key="'field_' + stepId" />
      </ion-col>
    </ion-row>
  </ion-grid>
</template>

<script lang="ts">
import { IonGrid, IonRow, IonCol } from "@ionic/vue"
import { computed, ComputedRef, ref, toRef, defineComponent, onUpdated, nextTick } from "vue"
import Field from "@/components/field.vue"
import { DBServiceField, DBServiceFieldArea, DBServiceFieldList } from "@/types"
import { letterToPos } from "@/utils/helpers"
import { useStore } from "vuex"

export default defineComponent({
  name: "Grid",
  components: {
    IonGrid,
    IonRow,
    IonCol,
    Field,
  },
  props: {
    stepdata: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const store = useStore()
    const maxRespGridCols = ref(12)
    const stepdata = toRef(props, "stepdata")
    const stepDataItems: ComputedRef<any> = computed(() => {
      return stepdata.value.items
    })

    const loadingControllers: ComputedRef<any[]> = computed(() => {
      return store.getters["main/getLoadingControllers"]
    })


    const stepId: ComputedRef<any> = computed(() => {
      return store.getters["main/getStepId"]
    })

    const rows: ComputedRef<number> = computed(() => {
      if (stepdata.value) {
        return parseInt(stepdata.value.grid.split(":")[0].substring(1))
      }
      return 0
    })

    const cols: ComputedRef<number> = computed(() => {
      if (stepdata.value) {
        return letterToPos(stepdata.value.grid.charAt(0))
      }
      return 0
    })

    const resetLoadingControllers = () => {
      if (loadingControllers.value.length) {
        for (const lc of loadingControllers.value) {
          lc.dismiss()
        }
      }
      store.commit("main/setLoadingControllers")
      const loadingElement = document.querySelectorAll("ion-loading")
      loadingElement.forEach((lc) => {
        lc.remove()
      })
    }

    const getFieldColumn = (field: DBServiceField | DBServiceFieldArea | DBServiceFieldList): number => {
      if (!field.pos) {
        return -1
      }
      return letterToPos(field.pos.charAt(0))
    }

    const getFieldRow = (field: DBServiceField | DBServiceFieldArea | DBServiceFieldList): number => {
      if (!field.pos) {
        return -1
      }
      return parseInt(field.pos.split(":")[0].substring(1))
    }

    const getFieldSpan = (field: DBServiceField | DBServiceFieldArea | DBServiceFieldList): number => {
      if (field.pos.includes(":")) {
        const startCol = getFieldColumn(field)
        const endCol = letterToPos(field.pos.substr(-1))
        return endCol - startCol
      }
      return 0
    }

    const getFieldsForRow = (row: number): (DBServiceField | DBServiceFieldArea | DBServiceFieldList)[] => {
      const fields: (DBServiceField | DBServiceFieldArea | DBServiceFieldList)[] = []
      const currRow = row + 1
      if (stepdata.value) {
        for (const field of stepDataItems.value) {
          const fieldRow = getFieldRow(field)
          if (fieldRow === currRow) {
            fields.push(field)
          }
        }
        return fields
      }
      return []
    }

    const gridWithFields: ComputedRef = computed(() => {
      const grid: any = []
      for (let rowIdx = 0; rowIdx < rows.value; rowIdx++) {
        const gridRow: (
          | (number | DBServiceField | DBServiceFieldArea | DBServiceFieldList | number)[]
          | string
        )[] = []
        if (stepdata.value && stepDataItems.value) {
          const gridCols = letterToPos(stepdata.value.grid.charAt(0))
          const fields = getFieldsForRow(rowIdx)
          let lastFieldSpan = 0
          for (let colIdx = 0; colIdx < gridCols; colIdx++) {
            let emptyCol = true
            if (lastFieldSpan) {
              lastFieldSpan--
              continue
            }
            for (const field of fields) {
              const fieldColIdx = getFieldColumn(field) - 1
              if (colIdx === fieldColIdx) {
                lastFieldSpan = getFieldSpan(field)
                gridRow.push([colIdx, field, lastFieldSpan])
                emptyCol = false
              }
            }
            if (emptyCol) {
              gridRow.push("empty")
            }
          }
        }
        grid.push(gridRow)
      }
      return grid
    })

    const getFieldSize = (column: string | (number | DBServiceField | number)[]): string => {
      const span = column === "empty" ? -1 : column[2]
      if (stepdata.value && span !== -1) {
        const stepGridCols = letterToPos(String(stepdata.value.grid.charAt(0)))
        const respGridColsSections = maxRespGridCols.value / stepGridCols
        const colspan = respGridColsSections * ((span as number) + 1)
        return String(colspan)
      }
      return String(maxRespGridCols.value / cols.value)
    }

    const updateDataForField = (field: DBServiceField, value: any) => {
      emit("valueChanged", field, value)
    }

    onUpdated(() => {
      nextTick(() => {
        resetLoadingControllers()
      })
    })

    return {
      stepId,
      maxRespGridCols,
      gridWithFields,
      updateDataForField,
      getFieldSize,
    }
  },
});
</script>

<style lang="scss" scoped>
.form-grid {
  margin: 0 2vw 5vh;
}
</style>